import { LightbulbIcon } from "@truevault/aquilo/src/components/selectMenu/SelectMenu";
import { SimpleCheckboxControlled, TextAreaBase, TextInputBase, ToolTip } from "@truevault/aquilo";
import clsx from "clsx";
import React, { useMemo, useState } from "react";
import {
  DataRetentionPolicyDetailCategory,
  RetentionOptions,
} from "../../../../common/service/server/dto/DataRetentionPolicyDto";
import { CategoryDto } from "../../../../common/service/server/dto/datamap/DataMapDto";
import { DATA_RETENTION_CATEGORIES } from "./dataRetentionCategories";
import {
  BUILTIN_STEPS,
  canChoosePeriod,
  draftDescription,
  PERIOD_OPTIONS,
  PurposeDraft,
  startingCustomText,
  suggestedPicIds,
} from "./retentionPolicySteps";

// The open state of one retention purpose: what data it covers and how long it is kept. Fully
// controlled — the builder owns the draft so the preview can show it while it is being edited.
export function RetentionPurposeEditor({
  type,
  draft,
  onChange,
  collectedPic,
  controlPrefix,
}: {
  type: DataRetentionPolicyDetailCategory | null;
  draft: PurposeDraft;
  onChange: (draft: PurposeDraft) => void;
  collectedPic: CategoryDto[];
  controlPrefix: string;
}) {
  const choosesPeriod = canChoosePeriod(type);
  const builtinText = type ? DATA_RETENTION_CATEGORIES[type].description : "";
  const [isEditingStatement, setIsEditingStatement] = useState(
    !choosesPeriod && draft.customText.trim() !== builtinText.trim(),
  );

  const suggested = useMemo(
    () => new Set(suggestedPicIds(type, collectedPic)),
    [type, collectedPic],
  );
  // Suggested categories lead, each group keeping the data map's order.
  const orderedPic = useMemo(
    () => [
      ...collectedPic.filter((pic) => suggested.has(pic.id)),
      ...collectedPic.filter((pic) => !suggested.has(pic.id)),
    ],
    [collectedPic, suggested],
  );

  const togglePic = (id: string, checked: boolean) => {
    const next = new Set(draft.pic);
    if (checked) {
      next.add(id);
    } else {
      next.delete(id);
    }
    onChange({
      ...draft,
      pic: collectedPic.map((pic) => pic.id).filter((picId) => next.has(picId)),
    });
  };

  const choosePeriod = (period: RetentionOptions) => {
    if (period === draft.period) {
      return;
    }
    const startingText = period === "CUSTOM" ? startingCustomText(type, draft) : draft.customText;
    onChange({ ...draft, period, customText: startingText });
  };

  const purposeName = type ? BUILTIN_STEPS[type].title.toLowerCase() : "this purpose";

  return (
    <div className="flex flex-col gap-[28px]">
      {type == null && (
        <div>
          <label
            htmlFor={`${controlPrefix}-label`}
            className="block text-[14px] font-medium leading-[20px] text-neutral-900"
          >
            Purpose name
          </label>
          <div className="mt-[2px] mb-[8px] text-[13px] leading-[20px] text-neutral-500">
            Keep it short. Your policy lists it as “[name], including [the data you select]”.
          </div>
          <TextInputBase
            field={`${controlPrefix}-label`}
            placeholder="e.g. Data we collect for warranty claims"
            inputTextSize="text-[14px]"
            value={draft.label}
            onChange={(e) => onChange({ ...draft, label: e.target.value ?? "" })}
          />
        </div>
      )}

      <div>
        <div className="text-[14px] font-medium leading-[20px] text-neutral-900">
          What data do you collect for this?
        </div>
        {suggested.size > 0 && (
          <div className="mt-[2px] text-[13px] leading-[20px] text-neutral-500">
            We’ve selected what businesses usually collect here. Adjust it to match yours.
          </div>
        )}
        <div className="mt-[12px] grid grid-cols-2 gap-x-[8px] gap-y-[4px]">
          {orderedPic.map((pic) => {
            const isSuggested = suggested.has(pic.id);
            return (
              <div
                key={pic.id}
                className={clsx(
                  "flex items-center gap-[6px] rounded-[6px] px-[10px] py-[6px]",
                  isSuggested && "bg-primary-50",
                )}
              >
                <SimpleCheckboxControlled
                  name={`${controlPrefix}-${pic.id}`}
                  label={pic.name}
                  checked={draft.pic.includes(pic.id)}
                  onChange={(checked) => togglePic(pic.id, checked)}
                />
                {isSuggested && (
                  <ToolTip text={`Usually collected for ${purposeName}`} inline>
                    {/* The sparkle bulb Data Flows uses for its recommendations. */}
                    <span className="inline-flex" aria-label="Suggested">
                      {LightbulbIcon}
                    </span>
                  </ToolTip>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <div className="text-[14px] font-medium leading-[20px] text-neutral-900">
          How long do you keep it?
        </div>
        {type === "SIGN_UP_FOR_PROMOTIONAL_AND_MARKETING" && (
          <div className="mt-[2px] text-[13px] leading-[20px] text-neutral-500">
            It is recommended not to retain data longer than 3 years for marketing purposes.
          </div>
        )}

        {choosesPeriod && (
          <div
            role="radiogroup"
            aria-label="How long do you keep it?"
            className="mt-[12px] flex flex-wrap gap-[8px]"
          >
            {PERIOD_OPTIONS.map((option) => {
              const isSelected = draft.period === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => choosePeriod(option.value)}
                  className={clsx(
                    "h-[34px] rounded-full border border-solid px-[14px] text-[14px] font-medium transition-colors duration-150",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2",
                    isSelected
                      ? "border-primary-600 bg-primary-50 text-primary-700"
                      : "cursor-pointer border-neutral-300 bg-white text-neutral-700 hover:border-primary-400 hover:text-primary-700",
                  )}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        )}

        <div className="mt-[16px]">
          {draft.period === "CUSTOM" && (choosesPeriod || isEditingStatement) ? (
            <>
              <TextAreaBase
                field={`${controlPrefix}-description`}
                rows={4}
                style={{ fontSize: 14, lineHeight: "22px" }}
                value={draft.customText}
                placeholder={
                  type
                    ? "Describe how long you keep this data and why"
                    : "e.g. We keep warranty records for 3 years after your purchase."
                }
                onChange={(e) => onChange({ ...draft, customText: e.target.value })}
              />
              {!choosesPeriod && (
                <button
                  type="button"
                  className="mt-[8px] cursor-pointer border-0 bg-transparent p-0 text-[13px] font-medium text-primary-600 hover:underline"
                  onClick={() => {
                    onChange({ ...draft, customText: builtinText });
                    setIsEditingStatement(false);
                  }}
                >
                  Use the suggested statement
                </button>
              )}
            </>
          ) : (
            <StatementBox
              text={draftDescription(type, draft)}
              onEdit={
                choosesPeriod
                  ? undefined
                  : () => {
                      setIsEditingStatement(true);
                    }
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}

// The sentence exactly as the policy will print it, with the period called out.
export function StatementBox({ text, onEdit }: { text: string; onEdit?: () => void }) {
  return (
    <div className="rounded-[8px] border border-solid border-neutral-200 bg-neutral-0 px-[16px] py-[12px]">
      <div className="text-[12px] font-medium uppercase leading-[16px] tracking-[0.4px] text-neutral-500">
        In your privacy policy
      </div>
      <div className="mt-[6px] text-[14px] leading-[22px] text-neutral-800">
        <HighlightedPeriod text={text} />
      </div>
      {onEdit && (
        <button
          type="button"
          className="mt-[8px] cursor-pointer border-0 bg-transparent p-0 text-[13px] font-medium text-primary-600 hover:underline"
          onClick={onEdit}
        >
          Edit statement
        </button>
      )}
    </div>
  );
}

export function HighlightedPeriod({ text }: { text: string }) {
  const match = text.match(/(\d+ years|as long as necessary)/i);
  if (!match || match.index == null) {
    return <>{text}</>;
  }
  return (
    <>
      {text.slice(0, match.index)}
      <span className="font-medium text-primary-700">{match[0]}</span>
      {text.slice(match.index + match[0].length)}
    </>
  );
}
